/**
 * What the update handover saw, reported when it went wrong (ADR 0067,
 * amended 2026-09-27).
 *
 * # Why
 *
 * The owner's installed app (Android Chrome, from the home screen) showed "A
 * new version of OLS Mail is ready" again after every Reload -- about 13 app
 * starts in 20 minutes -- until the site was reloaded in an ordinary tab. It
 * did not reproduce anywhere else, and nothing from the phone survived. The
 * likeliest story is that the waiting worker never activated: with
 * `skipWaiting`, Chrome activates it only once the old worker has no events in
 * flight. These two reports are there so the next occurrence says where it
 * stuck, instead of that being guessed again:
 *
 * - `pwa_handover_timeout`: `applyUpdateAndReload` ran out
 *   `HANDOVER_TIMEOUT_MS` (no `controllerchange`, no `activated`) and is about
 *   to reload anyway. Sent just before that reload, with `keepalive`.
 * - `pwa_update_loop`: the page that reload produced finds a worker waiting
 *   again within {@link LOOP_WINDOW_MS} of the press. Carries the previous
 *   handover's trace from sessionStorage, so it is useful even if the timeout
 *   report was lost.
 *
 * # What they hold
 *
 * Worker states with millisecond offsets, booleans, counts, the worker
 * script's PATH (`/sw.js`), whether the app runs standalone, the browser's
 * brand list, and -- via the reporter -- the route template, build hash and
 * user-agent string. Never an address, an id or a URL with anything in it.
 *
 * # Limits
 *
 * At most one report of each kind per page load, and one per
 * {@link REPORT_COOLDOWN_MS} per tab (sessionStorage). A loop therefore costs
 * the log two lines per ten minutes, not one per reload. Every storage access
 * is in a try/catch: without storage there is no loop detection at all, and
 * the timeout report falls back to once per page load (which a reload bounds
 * anyway).
 *
 * The handover itself is not changed by any of this.
 */

import { reportDiagnostic } from '@/errorReporting/reporter';

/** A reload that finds a worker waiting within this long of the press is a loop. */
export const LOOP_WINDOW_MS = 60_000;
/** One report of each kind per tab in this long. */
export const REPORT_COOLDOWN_MS = 10 * 60 * 1000;

/** The last Reload press: see {@link PressRecord}. */
export const PRESS_KEY = 'ols:pwa-reload-press';
export const TIMEOUT_REPORTED_KEY = 'ols:pwa-timeout-reported';
export const LOOP_REPORTED_KEY = 'ols:pwa-loop-reported';

const MAX_STATES = 16;
const MAX_SKIP_WAITING = 8;

type WorkerState = ServiceWorker['state'];
export type HandoverTrigger = 'user' | 'launch';
/** How a handover ended; `unknown` while it has not. */
export type HandoverOutcome = 'timeout' | 'controllerchange' | 'activated' | 'redundant' | 'unknown';

interface StateAt {
  worker: number;
  state: WorkerState;
  at_ms: number;
}

interface SkipWaitingPost {
  worker: number;
  at_ms: number;
  ok: boolean;
}

interface WorkerSlot {
  state: WorkerState;
  same_script: boolean;
  is_target: boolean;
}

/** The wire shape; matches `pwaDiagnosticDetails` in the backend exactly. */
export interface HandoverDetails {
  trigger: HandoverTrigger;
  outcome: HandoverOutcome;
  standalone: boolean;
  ua_brands: string;
  ua_mobile: boolean | null;
  ua_platform: string;
  since_load_ms: number;
  since_press_ms: number;
  target_script: string;
  target_states: StateAt[];
  skip_waiting: SkipWaitingPost[];
  replacements: number;
  registration: { active: WorkerSlot | null; waiting: WorkerSlot | null; installing: WorkerSlot | null };
  controller: { present: boolean; is_active: boolean; state: WorkerState | ''; same_script: boolean };
  loops: number;
}

/**
 * What a Reload press leaves in sessionStorage for the page after it: when,
 * for which worker script, how the handover went, and how many loops in a row
 * this tab has seen.
 */
interface PressRecord {
  at: number;
  script: string;
  trigger: HandoverTrigger;
  outcome: HandoverOutcome;
  states: StateAt[];
  skip: SkipWaitingPost[];
  replacements: number;
  /** Consecutive presses that each came back to a waiting worker. */
  loops: number;
  /** The page after this press has already counted it as a loop. */
  detected: boolean;
}

/* -------------------------------------------------------------- helpers */

function now(): number {
  return Date.now();
}

function perfNow(): number {
  try {
    return typeof performance !== 'undefined' ? performance.now() : 0;
  } catch {
    return 0;
  }
}

function storage(): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : undefined;
  } catch {
    return undefined;
  }
}

function readJSON(key: string): unknown {
  try {
    const raw = storage()?.getItem(key);
    return raw ? (JSON.parse(raw) as unknown) : undefined;
  } catch {
    return undefined;
  }
}

function write(key: string, value: string): boolean {
  try {
    const s = storage();
    if (s === undefined) return false;
    s.setItem(key, value);
    return s.getItem(key) === value;
  } catch {
    return false;
  }
}

/** The worker script's path only -- never its query, never an origin. */
export function scriptPath(worker: ServiceWorker | null | undefined): string {
  try {
    if (!worker?.scriptURL) return '';
    const path = new URL(worker.scriptURL, 'https://x.invalid').pathname;
    return /^\/[A-Za-z0-9._/-]{1,64}$/.test(path) ? path : '';
  } catch {
    return '';
  }
}

function isStandalone(): boolean {
  try {
    if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
    return (navigator as Navigator & { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
}

interface UAData {
  brands?: { brand: string; version: string }[];
  mobile?: boolean;
  platform?: string;
}

function clientHints(): { ua_brands: string; ua_mobile: boolean | null; ua_platform: string } {
  try {
    const data = (navigator as Navigator & { userAgentData?: UAData }).userAgentData;
    if (data === undefined) return { ua_brands: '', ua_mobile: null, ua_platform: '' };
    const brands = (data.brands ?? [])
      .map((b) => `${b.brand} ${b.version}`)
      .join('; ')
      .replace(/[^A-Za-z0-9 ._;()/-]/g, '_')
      .slice(0, 200);
    const platform = String(data.platform ?? '')
      .replace(/[^A-Za-z0-9 ._-]/g, '_')
      .slice(0, 32);
    return {
      ua_brands: brands,
      ua_mobile: typeof data.mobile === 'boolean' ? data.mobile : null,
      ua_platform: platform,
    };
  } catch {
    return { ua_brands: '', ua_mobile: null, ua_platform: '' };
  }
}

function slot(worker: ServiceWorker | null | undefined, script: string, target: ServiceWorker | null): WorkerSlot | null {
  if (worker == null) return null;
  return {
    state: worker.state,
    same_script: script !== '' && scriptPath(worker) === script,
    is_target: target !== null && worker === target,
  };
}

/** The registration and the controller as they are now. */
function snapshot(
  registration: ServiceWorkerRegistration | undefined,
  container: ServiceWorkerContainer | undefined,
  script: string,
  target: ServiceWorker | null,
): Pick<HandoverDetails, 'registration' | 'controller'> {
  let controller: ServiceWorker | null;
  try {
    controller = container?.controller ?? null;
  } catch {
    controller = null;
  }
  return {
    registration: {
      active: slot(registration?.active, script, target),
      waiting: slot(registration?.waiting, script, target),
      installing: slot(registration?.installing, script, target),
    },
    controller: {
      present: controller !== null,
      is_active: controller !== null && controller === (registration?.active ?? null),
      state: controller?.state ?? '',
      same_script: controller !== null && script !== '' && scriptPath(controller) === script,
    },
  };
}

/** Once per page load, then once per {@link REPORT_COOLDOWN_MS} per tab. */
function claimReport(key: string, sentThisPage: { value: boolean }, requireStorage: boolean): boolean {
  if (sentThisPage.value) return false;
  const last = Number(readJSON(key));
  const at = now();
  if (Number.isFinite(last) && last > 0 && at - last < REPORT_COOLDOWN_MS) return false;
  if (!write(key, String(at)) && requireStorage) return false;
  sentThisPage.value = true;
  return true;
}

const timeoutSent = { value: false };
const loopSent = { value: false };
let loopChecked = false;

function readPress(): PressRecord | undefined {
  const value = readJSON(PRESS_KEY);
  if (typeof value !== 'object' || value === null) return undefined;
  const r = value as Partial<PressRecord>;
  if (typeof r.at !== 'number' || !Number.isFinite(r.at)) return undefined;
  return {
    at: r.at,
    script: typeof r.script === 'string' ? r.script : '',
    trigger: r.trigger === 'launch' ? 'launch' : 'user',
    outcome: r.outcome ?? 'unknown',
    states: Array.isArray(r.states) ? r.states.slice(0, MAX_STATES) : [],
    skip: Array.isArray(r.skip) ? r.skip.slice(0, MAX_SKIP_WAITING) : [],
    replacements: typeof r.replacements === 'number' ? r.replacements : 0,
    loops: typeof r.loops === 'number' ? r.loops : 0,
    detected: r.detected === true,
  };
}

function writePress(record: PressRecord): void {
  write(PRESS_KEY, JSON.stringify(record));
}

/* ------------------------------------------------------ the handover trace */

export interface HandoverTrace {
  /** The target's state, as `statechange` (or the first look) shows it. */
  noteState(worker: ServiceWorker): void;
  /** The handover now follows `next`, the waiting worker's replacement. */
  noteReplacement(next: ServiceWorker): void;
  /** SKIP_WAITING was posted to the current target; `ok` false if it threw. */
  noteSkipWaiting(ok: boolean): void;
  /**
   * The handover is over and the page is about to reload. Records the outcome
   * for the next page, and on `timeout` sends the report first.
   */
  finish(
    outcome: HandoverOutcome,
    registration: ServiceWorkerRegistration | undefined,
    container: ServiceWorkerContainer | undefined,
  ): void;
}

/**
 * Start watching one handover. Records the press in sessionStorage at once,
 * so the next page knows about it even if this one never gets to `finish`.
 */
export function startHandoverTrace(target: ServiceWorker, trigger: HandoverTrigger): HandoverTrace {
  const pressedAt = now();
  const pressedPerf = perfNow();
  const script = scriptPath(target);
  let current = target;
  let worker = 0;
  const states: StateAt[] = [];
  const skip: SkipWaitingPost[] = [];
  let finished = false;

  const previous = readPress();
  const record: PressRecord = {
    at: pressedAt,
    script,
    trigger,
    outcome: 'unknown',
    states,
    skip,
    replacements: 0,
    // A press made on the page a loop produced continues the count.
    loops: previous?.detected === true ? previous.loops : 0,
    detected: false,
  };

  const elapsed = (): number => Math.max(0, Math.round(perfNow() - pressedPerf));
  const noteState = (w: ServiceWorker): void => {
    if (w !== current) return;
    const last = states[states.length - 1];
    if (last !== undefined && last.worker === worker && last.state === w.state) return;
    if (states.length >= MAX_STATES) return;
    states.push({ worker, state: w.state, at_ms: elapsed() });
    writePress(record);
  };
  writePress(record);
  noteState(target);

  return {
    noteState,
    noteReplacement(next) {
      if (next === current) return;
      current = next;
      worker += 1;
      record.replacements = worker;
      noteState(next);
    },
    noteSkipWaiting(ok) {
      if (skip.length < MAX_SKIP_WAITING) skip.push({ worker, at_ms: elapsed(), ok });
      writePress(record);
    },
    finish(outcome, registration, container) {
      if (finished) return;
      finished = true;
      try {
        record.outcome = outcome;
        writePress(record);
        if (outcome !== 'timeout') return;
        if (!claimReport(TIMEOUT_REPORTED_KEY, timeoutSent, false)) return;
        const details: HandoverDetails = {
          trigger,
          outcome,
          standalone: isStandalone(),
          ...clientHints(),
          since_load_ms: Math.round(perfNow()),
          since_press_ms: elapsed(),
          target_script: script,
          target_states: states,
          skip_waiting: skip,
          replacements: worker,
          ...snapshot(registration, container, script, current),
          loops: record.loops,
        };
        reportDiagnostic('pwa_handover_timeout', 'PWA update handover timed out', details);
      } catch {
        // The reload that follows matters more than the report.
      }
    },
  };
}

/* ---------------------------------------------------------- loop detection */

/**
 * A worker is waiting (or the prompt is about to show). If this page is the
 * one a Reload press produced less than {@link LOOP_WINDOW_MS} ago, the
 * update did not take: count it, and report it (rate-limited).
 *
 * Call it BEFORE anything that might reload again (the silent update at
 * launch), so the report is on its way first. Safe to call repeatedly; only
 * the first call on a page does anything.
 */
export function checkForUpdateLoop(
  registration: ServiceWorkerRegistration | undefined,
  container: ServiceWorkerContainer | undefined,
): void {
  if (loopChecked) return;
  try {
    if (registration?.waiting == null) return;
    loopChecked = true;
    const press = readPress();
    if (press === undefined || press.detected) return;
    const since = now() - press.at;
    if (since < 0 || since > LOOP_WINDOW_MS) return;

    press.loops += 1;
    press.detected = true;
    writePress(press);

    if (!claimReport(LOOP_REPORTED_KEY, loopSent, true)) return;
    const details: HandoverDetails = {
      trigger: press.trigger,
      // How the previous handover ended, as that page recorded it.
      outcome: press.outcome,
      standalone: isStandalone(),
      ...clientHints(),
      since_load_ms: Math.round(perfNow()),
      since_press_ms: since,
      target_script: press.script,
      target_states: press.states,
      skip_waiting: press.skip,
      replacements: press.replacements,
      // A new page: no worker object here is the one pressed for.
      ...snapshot(registration, container, press.script, null),
      loops: press.loops,
    };
    reportDiagnostic('pwa_update_loop', 'PWA update loop: a new version is still waiting after Reload', details);
  } catch {
    // A diagnostic must never break the update it is watching.
  }
}

/** Test seam: forget this page's reports and checks. */
export function resetHandoverDiagnosticsForTests(): void {
  timeoutSent.value = false;
  loopSent.value = false;
  loopChecked = false;
}
